import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import type { Components } from 'react-markdown';
import { useTranslation } from '@hooks/useTranslation';
import styles from './AuditMarkdownBody.module.scss';

const Markdown = lazy(() => import('react-markdown'));

// Allow-list doubles as XSS guard — no raw HTML reaches the DOM.
const ALLOWED_ELEMENTS = [
  'p',
  'strong',
  'em',
  'a',
  'code',
  'del',
  'br',
  'ul',
  'ol',
  'li',
  'h3',
  'h4',
];

const MARKDOWN_COMPONENTS: Components = {
  a: ({ children, href, title }) => (
    <a href={href} title={title} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const LOCALE_TOKEN_RE = /%%([A-Z][A-Z0-9_]+)%%/g;
const LIST_LINE_RE = /^\s*[-*+]\s/;
const HEADING_LINE_RE = /^#{1,6}\s/;

function localizeTokens(line: string, t: (key: string) => string): string {
  return line.replace(LOCALE_TOKEN_RE, (_, key) => t(key));
}

function isTreeShape(lines: string[]): boolean {
  return lines.some((l) => LIST_LINE_RE.test(l) || HEADING_LINE_RE.test(l));
}

// Adjacent list lines join with \n (one list block, nesting preserved);
// every other boundary uses \n\n so paragraphs and the paragraph→list
// boundary actually break.
function joinForCommonMark(lines: string[]): string {
  return lines.reduce<string>((acc, line, i) => {
    if (i === 0) return line;
    const prev = lines[i - 1] ?? '';
    const sep = LIST_LINE_RE.test(line) && LIST_LINE_RE.test(prev) ? '\n' : '\n\n';
    return acc + sep + line;
  }, '');
}

interface AuditMarkdownBodyProps {
  /** Pre-trimmed, non-empty markdown lines. Caller filters; component does not. */
  lines: string[];
}

export function AuditMarkdownBody({ lines }: AuditMarkdownBodyProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(false);
  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);

  const localizedLines = useMemo(
    () => lines.map((line) => localizeTokens(line, t)),
    [lines, t],
  );

  const treeShape = useMemo(() => isTreeShape(localizedLines), [localizedLines]);
  const treeDoc = useMemo(
    () => (treeShape ? joinForCommonMark(localizedLines) : ''),
    [treeShape, localizedLines],
  );

  if (localizedLines.length === 0) return null;

  // One Markdown call on the whole doc — list nesting needs context across
  // lines; per-line parsing flattens it.
  if (treeShape) {
    const fallback = <div className={styles.line}>{localizedLines[0] ?? ''}</div>;
    return (
      <div className={styles.body}>
        <Suspense fallback={fallback}>
          <div className={styles.tree}>
            <Markdown
              allowedElements={ALLOWED_ELEMENTS}
              unwrapDisallowed
              components={MARKDOWN_COMPONENTS}
            >
              {treeDoc}
            </Markdown>
          </div>
        </Suspense>
      </div>
    );
  }

  const [first, ...rest] = localizedLines;
  if (first === undefined) return null;

  const renderLine = (line: string, key: number) => (
    <div key={key} className={styles.line}>
      <Markdown
        allowedElements={ALLOWED_ELEMENTS}
        unwrapDisallowed
        components={MARKDOWN_COMPONENTS}
      >
        {line}
      </Markdown>
    </div>
  );

  const fallback = <div className={styles.line}>{first}</div>;

  if (rest.length === 0) {
    return (
      <div className={styles.body}>
        <Suspense fallback={fallback}>{renderLine(first, 0)}</Suspense>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <Suspense fallback={fallback}>
        {expanded
          ? localizedLines.map((line, idx) => renderLine(line, idx))
          : renderLine(first, 0)}
      </Suspense>
      <button type="button" className={styles.toggle} onClick={toggleExpanded}>
        {expanded
          ? t('K_AUDIT_SHOW_LESS', 'Show less')
          : t('K_AUDIT_MORE_CHANGES', '+{n} more').replace('{n}', String(rest.length))}
      </button>
    </div>
  );
}
